// An artistic visualisation of the word association method, digital art, colorful, no text
// An artistic visualisation of a room setup for a meeting, digital art, colorful, no text. ABSOLUTELY NO TEXT. (abstract)

// Brainnetting
// How-Now-Wow Matrix
// Lotus Blossom Technique
// Starbursting Method
// Open innovation
// Hackathon
// Groupthink

export const TOOLBOX_CATEGORIES = ["preparation", "engagement", "ideation", "evaluation"] as const;
export type ToolboxCategory = (typeof TOOLBOX_CATEGORIES)[number];

export type ToolboxTagKind = "energizer" | "opener" | "closer" | "technique" | "tip";

export type ToolboxContentKind =
	| "purpose"
	| "instructions"
	| "materials"
	| "steps"
	| "tips"
	| "variations"
	| "debrief"
	| "examples"
	| "example"
	| "background"
	| "pitfalls"
	| "read-more";

export interface ToolboxItemContent {
	kind: ToolboxContentKind;
	text: string;
}

export type ToolboxItemId =
	| "rock-paper-scissors"
	| "scamper"
	| "brainwriting"
	| "mindmapping"
	| "reverse-thinking"
	| "role-storming"
	| "six-thinking-hats"
	| "question-storming"
	| "the-five-whys"
	| "analogies"
	| "nominal-group-technique"
	| "disney-method"
	| "room-setup"
	| "action-planning"
	| "icebreaker-questions"
	| "swot-analysis"
	| "elevator-pitch"
	| "business-model-canvas"
	| "value-proposition-canvas"
	| "idea-synthesis"
	| "crazy-eights"
	| "rapid-ideation"
	| "gamification"
	| "parking-lot"
	| "decision-matrix"
	| "pros-and-cons"
	| "affinity-grouping"
	| "smart-goals"
	| "diversity-bonuses"
	| "agenda"
	| "the-seven-ps"
	| "5w1h-method"
	| "combinatorial-creativity"
	| "pomodoro"
	| "dot-voting"
	| "music"
	| "comparison-voting"
	| "evaluation-voting"
	//| "budget-voting"
	| "meditation"
	| "walk-and-talk"
	| "speed-dating"
	| "human-bingo"
	| "word-associations";

export type ToolboxItemIdeamapSupport = "ai" | "tool";

export interface ToolboxItem {
	id: ToolboxItemId;
	name: string;
	category: ToolboxCategory;
	groupSize?: string;
	duration?: string;
	ideamapSupport?: ToolboxItemIdeamapSupport;
	tags?: ToolboxTagKind[];
}

export const TOOLBOX_PREPARATION_ITEMS: ToolboxItem[] = [
	{
		category: "preparation",
		name: /*@translate*/ "Agenda",
		id: "agenda",
		tags: ["tip"]
	},
	{
		category: "preparation",
		name: /*@translate*/ "The 7 P’s",
		id: "the-seven-ps",
		tags: ["technique"]
	},

	{
		category: "preparation",
		name: /*@translate*/ "SMART Goals",
		id: "smart-goals",
		tags: ["technique"]
	},
	{
		category: "preparation",
		name: /*@translate*/ "5W1H Method",
		id: "5w1h-method",
		tags: ["technique"]
	},
	{
		category: "preparation",
		name: /*@translate*/ "Diversity Bonuses",
		id: "diversity-bonuses",
		tags: ["tip"]
	},
	{
		category: "preparation",
		name: /*@translate*/ "Pomodoro",
		id: "pomodoro",
		ideamapSupport: "tool",
		tags: ["technique"]
	},
	{
		category: "preparation",
		name: /*@translate*/ "Room Setup",
		id: "room-setup",
		tags: ["tip"]
	}
];

export const TOOLBOX_IDEATION_ITEMS: ToolboxItem[] = [
	{
		category: "ideation",
		name: /*@translate*/ "SCAMPER",
		id: "scamper",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Six Thinking Hats",
		id: "six-thinking-hats",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Brainwriting",
		id: "brainwriting",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Mindmapping",
		id: "mindmapping",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Reverse Thinking",
		id: "reverse-thinking",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "ideation",
		name: /*@translate*/ "Role Storming",
		id: "role-storming",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "ideation",
		name: /*@translate*/ "Analogies and Metaphors",
		id: "analogies",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Question Storming",
		id: "question-storming",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "The Five Whys",
		id: "the-five-whys",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "ideation",
		name: /*@translate*/ "Disney Method",
		id: "disney-method",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Word Associations",
		id: "word-associations",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "ideation",
		name: /*@translate*/ "Idea Synthesis",
		id: "idea-synthesis",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Crazy Eights",
		ideamapSupport: "ai",
		id: "crazy-eights",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Rapid Ideation",
		id: "rapid-ideation",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Parking Lot",
		id: "parking-lot",
		tags: ["tip"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Gamification",
		id: "gamification",
		ideamapSupport: "ai",
		tags: ["tip"]
	},
	{
		category: "ideation",
		name: /*@translate*/ "Combinatorial Creativity",
		id: "combinatorial-creativity",
		tags: ["tip"],
		ideamapSupport: "ai"
	}
];

export const TOOLBOX_ENGAGEMENT_ITEMS: ToolboxItem[] = [
	{
		category: "engagement",
		name: /*@translate*/ "Icebreaker Questions",
		id: "icebreaker-questions",
		tags: ["technique"],
		ideamapSupport: "ai",
		groupSize: "4-8",
		duration: "10-15 min"
	},
	{
		category: "engagement",
		name: /*@translate*/ "Meditation",
		id: "meditation",
		duration: "10 min",
		groupSize: "2+",
		tags: ["opener"]
	},
	{
		category: "engagement",
		name: /*@translate*/ "Speed Dating",
		id: "speed-dating",
		duration: "20 min",
		groupSize: "8-20",
		tags: ["opener"]
	},
	{
		category: "engagement",
		name: /*@translate*/ "Walk and Talk",
		id: "walk-and-talk",
		duration: "20-30 min",
		tags: ["energizer"],
		groupSize: "2+"
	},
	{
		category: "engagement",
		name: /*@translate*/ "Human Bingo",
		id: "human-bingo",
		duration: "15 min",
		groupSize: "10-50",
		tags: ["energizer"]
	},
	/*{
		category: "engagement",
		name: "Rock, Paper, Scissors",
		id: "rock-paper-scissors",
		groupSize: "2+",
		duration: "5-10 min",
		tags: ["energizer"],
		content: [
			{
				kind: "purpose",
				text: "Energize participants and get them moving."
			},
			{
				kind: "instructions",
				text: "Participants pair up and play rock, paper, scissors. The winner finds a new partner and plays again. Repeat until facilitator calls time."
			},
			{
				kind: "purpose",
				text: ``
			},
			{
				kind: "steps",
				text: ``
			},
			{
				kind: "tips",
				text: ``
			},
			{
				kind: "pitfalls",
				text: ``
			},
			{
				kind: "read-more",
				text: `* []()
* []()
* []()
* []()
* []()`
			}
		]
	},*/
	{
		category: "engagement",
		name: /*@translate*/ "Music",
		id: "music",
		tags: ["tip"],
		ideamapSupport: "tool"
	}
];

export const TOOLBOX_EVALUATION_ITEMS: ToolboxItem[] = [
	{
		category: "evaluation",
		name: /*@translate*/ "SWOT Analysis",
		id: "swot-analysis",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Business Model Canvas",
		id: "business-model-canvas",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Value Proposition Canvas",
		id: "value-proposition-canvas",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Pros and Cons",
		id: "pros-and-cons",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Affinity Grouping",
		id: "affinity-grouping",
		tags: ["technique"],
		ideamapSupport: "ai"
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Elevator Pitch",
		id: "elevator-pitch",
		ideamapSupport: "ai",
		tags: ["technique"]
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Dot Voting",
		id: "dot-voting",
		tags: ["technique"],
		ideamapSupport: "tool"
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Comparison Voting",
		id: "comparison-voting",
		tags: ["technique"],
		ideamapSupport: "tool"
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Evaluation Voting",
		id: "evaluation-voting",
		tags: ["technique"],
		ideamapSupport: "tool"
	},
	/*{
		category: "evaluation",
		name: "Budget Voting",
		id: "budget-voting",
		tags: ["technique"],
		content: [
			{
				kind: "purpose",
				text: ``
			},
			{
				kind: "steps",
				text: ``
			},
			{
				kind: "tips",
				text: ``
			},
			{
				kind: "pitfalls",
				text: ``
			},
			{
				kind: "read-more",
				text: `* []()
* []()
* []()
* []()
* []()`
			}
		]
	},*/
	{
		category: "evaluation",
		name: /*@translate*/ "Decision Matrix",
		id: "decision-matrix",
		ideamapSupport: "tool",
		tags: ["technique"]
	},
	{
		category: "evaluation",
		name: /*@translate*/ "Action Planning",
		id: "action-planning",
		tags: ["technique"]
	}
];

export const TOOLBOX_ITEMS_MAP: { [key: string]: ToolboxItem[] } = {
	preparation: TOOLBOX_PREPARATION_ITEMS,
	engagement: TOOLBOX_ENGAGEMENT_ITEMS,
	ideation: TOOLBOX_IDEATION_ITEMS,
	evaluation: TOOLBOX_EVALUATION_ITEMS
};
